/**
 * Concierge — Team assignments (PROD-6787). Standalone page reached from the "Concierge Team"
 * heading on /concierge-pods. Shows the lifestyle leads assigned to each concierge advisor in a
 * date range (default: today, IST), with a date-range filter and an optional per-concierge filter.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../util_components/common_header';
import CustomLoading from '../../util_components/custom_loading';
import Button from '../../util_components/button';
import { get_api_url } from '../../../utils/urls';
import { get_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';

const BASE = '/v2/cms_user/concierge_pods';
const PAGE_STYLE = {
  background: 'var(--surface-page)',
  color: 'var(--text-primary)',
  minHeight: '100%'
};
const CARD_STYLE = { background: 'var(--surface-card)', borderColor: 'var(--border-default)' };
const MUTED_STYLE = { color: 'var(--text-muted)' };
const INPUT_STYLE = {
  background: 'var(--input-bg)',
  borderColor: 'var(--input-border)',
  color: 'var(--input-text)'
};

class ConciergePodsTeamAssignments extends Component {
  state = { from: '', to: '', teacher_uuid: '', loading: false, data: null, advisors: [] };

  componentDidMount() {
    const today = moment().tz('Asia/Kolkata').format('YYYY-MM-DD');
    this.setState({ from: today, to: today }, () => {
      this.load_advisors();
      this.load();
    });
  }

  load_advisors = () => {
    const { set_notification_variable } = this.props;
    get_api(get_api_url(`${BASE}/concierge_teachers`), true)
      .then((res) => this.setState({ advisors: (res.data && res.data.data) || [] }))
      .catch((e) => {
        let msg = 'Failed to load the concierge filter list';
        if (e && e.response && e.response.data) msg = e.response.data.reason || msg;
        set_notification_variable(true, 'error', msg);
      });
  };

  load = () => {
    const { from, to, teacher_uuid } = this.state;
    const { set_notification_variable } = this.props;
    if (!from || !to) return;
    this.setState({ loading: true });
    let url = `${BASE}/team_assignments?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    if (teacher_uuid) url += `&teacher_uuid=${encodeURIComponent(teacher_uuid)}`;
    get_api(get_api_url(url), true)
      .then((res) => this.setState({ data: (res.data && res.data.data) || null, loading: false }))
      .catch((e) => {
        let msg = 'Failed to load team assignments';
        if (e && e.response && e.response.data) msg = e.response.data.reason || msg;
        set_notification_variable(true, 'error', msg);
        this.setState({ loading: false });
      });
  };

  on_change = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  render_student = (s) => (
    <li key={s.uuid} className="text-xs" style={MUTED_STYLE}>
      <a
        href={`/studentprofile?id=${encodeURIComponent(s.uuid)}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'var(--brand-primary)' }}
      >
        {s.name || s.email || s.uuid}
      </a>
      {s.email ? ` · ${s.email}` : ''}
      {' · '}
      {moment(s.assigned_at).tz('Asia/Kolkata').format('MMM D, h:mm A [IST]')}
      {s.reason ? ` · ${s.reason}` : ''}
    </li>
  );

  render_team = (team) => (
    <div key={team.teacher.uuid} className="rounded-lg border p-3" style={CARD_STYLE}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{team.teacher.name || team.teacher.email}</span>
        <span className="text-xs font-semibold" style={MUTED_STYLE}>
          {team.count} lead{team.count === 1 ? '' : 's'}
        </span>
      </div>
      {team.count > 0 ? (
        <ul className="mt-2 flex flex-col gap-0.5">
          {(team.students || []).map(this.render_student)}
        </ul>
      ) : (
        <div className="mt-1 text-xs" style={MUTED_STYLE}>
          No leads in this range.
        </div>
      )}
    </div>
  );

  render() {
    const { from, to, teacher_uuid, loading, data, advisors } = this.state;
    const teams = (data && data.teams) || [];
    return (
      <div style={PAGE_STYLE}>
        <CommonHeader title="Concierge — Team assignments" loading={loading} />
        <div className="mx-auto max-w-4xl px-3 py-4 sm:px-6">
          <Link to="/concierge-pods" className="text-sm" style={{ color: 'var(--brand-primary)' }}>
            &larr; Back to Concierge Pods
          </Link>

          <div className="mt-3 flex flex-col flex-wrap gap-2 sm:flex-row sm:items-end">
            <label className="text-xs" style={MUTED_STYLE}>
              From
              <input
                type="date"
                name="from"
                value={from}
                onChange={this.on_change}
                className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-40"
                style={INPUT_STYLE}
              />
            </label>
            <label className="text-xs" style={MUTED_STYLE}>
              To
              <input
                type="date"
                name="to"
                value={to}
                onChange={this.on_change}
                className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-40"
                style={INPUT_STYLE}
              />
            </label>
            <label className="text-xs" style={MUTED_STYLE}>
              Concierge
              <select
                name="teacher_uuid"
                value={teacher_uuid}
                onChange={this.on_change}
                className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-56"
                style={INPUT_STYLE}
              >
                <option value="">All advisors</option>
                {advisors.map((a) => (
                  <option key={a.uuid} value={a.uuid}>
                    {a.first_name} {a.last_name}
                  </option>
                ))}
              </select>
            </label>
            <Button onClick={this.load} custom_style={{ padding: '6px 14px', fontSize: '13px' }}>
              Apply
            </Button>
          </div>

          {loading && <CustomLoading />}
          {!loading && data && (
            <div className="mt-4 flex flex-col gap-2">
              <div className="text-xs" style={MUTED_STYLE}>
                Showing {from} → {to} (IST) · {teams.length} advisor{teams.length === 1 ? '' : 's'}
              </div>
              {teams.map(this.render_team)}
            </div>
          )}
        </div>
      </div>
    );
  }
}

ConciergePodsTeamAssignments.propTypes = {
  set_notification_variable: PropTypes.func.isRequired
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) =>
    dispatch(actions.set_notification_variable(show, type, mes))
});

export default connect(mapStateToProps, mapDispatchToProps)(ConciergePodsTeamAssignments);
